import {
    Button,
    Card,
    Modal,
    Table,
    TableBody,
    TableContainer,
    TableHead,
    TableHeader,
    TableRow,
} from '@ksyos/design-system';
import { useEffect, useState } from 'react';
import type { IOrganizationPatientCountLogFrontEnd } from '@common/interfaces/organizationPatientCountLog';
import type { IOrganizationPatientCount } from '@frontend/components/interfaces/organizationPatientCount';
import { ManagerAuthorization } from '@common/enums';
import { translateKey } from '@frontend/translate';
import { hasOneAuthorization } from '@frontend/utils';
import { useAbortableFetch } from '@frontend/utils/hooks/useAbortableFetch';
import { LogElement } from './OrganizationPatientCountLogElement';

type OrganizationPatientCountLogsModalProps = {
    currentPatientCounts: IOrganizationPatientCount[];
    organizationId: string;
};

export function OrganizationPatientCountLogsModal(props: OrganizationPatientCountLogsModalProps) {
    const abortableFetch = useAbortableFetch();
    const [showLogModal, setShowLogModal] = useState(false);
    const [patientCountLogs, setPatientCountLogs] = useState<IOrganizationPatientCountLogFrontEnd[]>([]);
    const canRead = hasOneAuthorization(ManagerAuthorization.organizationRead);

    useEffect(() => {
        loadOrganizationPatientCountLogs();
    }, [props.currentPatientCounts]);

    if (!canRead || patientCountLogs.length === 0) {
        return null;
    }

    return (
        <Card.Footer>
            <Button
                onClick={() => {
                    setShowLogModal(true);
                }}
                sx={{ marginRight: 'auto' }}
                variant="link"
            >
                {translateKey('bankAccountDetailsLog.viewChangesLog')}
            </Button>
            <Modal
                isOpen={showLogModal}
                onClose={() => setShowLogModal(false)}
                size="xlarge"
            >
                <Modal.Header>{translateKey('organization.modal.logbookTitle')}</Modal.Header>
                <Modal.CloseButton />
                <Modal.Body>
                    <TableContainer>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableHeader>{translateKey('organization.modal.logbookDateOfChange')}</TableHeader>
                                    <TableHeader>{translateKey('organization.modal.logbookChangedBy')}</TableHeader>
                                    <TableHeader>{translateKey('organization.modal.logbookAction')}</TableHeader>
                                    <TableHeader>{translateKey('organization.referenceDate')}</TableHeader>
                                    <TableHeader>{translateKey('organization.newCount')}</TableHeader>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {patientCountLogs.map((log) => (
                                    <LogElement
                                        {...log}
                                        key={log.id}
                                    />
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Modal.Body>
                <Modal.Footer />
            </Modal>
        </Card.Footer>
    );

    async function loadOrganizationPatientCountLogs() {
        const url = `/api/organizations/${props.organizationId}/organizationPatientCountLogs`;
        const result = (await abortableFetch.getJson(url)).json.item as IOrganizationPatientCountLogFrontEnd[];
        setPatientCountLogs(result);
    }
}
